import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, Observable, firstValueFrom, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { User, UserRole } from 'src/app/features/admin/types/user.type';
import { QueryFilterDTO } from 'src/app/shared/models/_requests/query-filter.dto';
import { omitUndefined } from 'src/app/shared/utils/user.helpers';
import {
  buildResultsQueryFilter,
  FinderAnswers,
  LanguageKey,
} from 'amuvee-shared';

interface LoginResponse {
  access_token: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _user$ = new BehaviorSubject<User | null>(null);
  public user$ = this._user$.asObservable();
  private apiUrl = `${environment.apiUrl}`;
  public user?: User;
  private accessToken?: string;

  constructor(
    private http: HttpClient,
    private router: Router,
    private translate: TranslateService,
  ) {
    this.loadToken();
  }

  login(email: string, password: string): Observable<any> {
    return this.http
      .post<LoginResponse>(`${this.apiUrl}/auth/login`, { email, password })
      .pipe(
        map((response) => {
          this.handleLoginSuccess(response);
          return response;
        }),
        catchError(this.handleError),
      );
  }

  logout(): void {
    this.clearSession();
    this.router.navigate(['/auth/login']);
  }

  // Public self-registration (email-first). No password, no roles — the
  // server forces roles:['User'] and emails an activation link.
  register(data: {
    email: string;
    name: string;
    consent: boolean;
    honeypot?: string;
  }): Observable<any> {
    return this.http
      .post(`${this.apiUrl}/auth/register`, data)
      .pipe(catchError(this.handleError));
  }

  // Set password from the activation link and auto-login with the returned JWT.
  activate(token: string, password: string): Observable<any> {
    return this.http
      .post<LoginResponse>(`${this.apiUrl}/auth/activate`, { token, password })
      .pipe(
        map((response) => {
          this.handleLoginSuccess(response);
          return response;
        }),
        catchError(this.handleError),
      );
  }

  resendVerification(email: string): Observable<any> {
    return this.http
      .post(`${this.apiUrl}/auth/resend-verification`, { email })
      .pipe(catchError(this.handleError));
  }

  // Pre-check the activation token before showing the set-password form.
  verifyActivationToken(token: string): Observable<any> {
    const params = new HttpParams().set('token', token);
    return this.http
      .get(`${this.apiUrl}/auth/verify`, { params })
      .pipe(catchError(this.handleError));
  }

  // Token Handling Methods

  // Callers (login.component.ts, activate.component.ts) always await their
  // own loadUser() right after — firing a second, unawaited one here raced
  // with it and could clobber a fresher user object with a stale one.
  private handleLoginSuccess(response: LoginResponse): void {
    this.accessToken = response.access_token;
    localStorage.setItem('token', this.accessToken);
    const decodedToken = this.decodeToken(this.accessToken);
    this.setRoles(decodedToken.roles);
  }

  private loadToken(): void {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      this.accessToken = token;
      this.setRoles(this.decodeToken(token).roles);
    } catch {
      // Corrupt or non-JWT token would throw jwtDecode and break app bootstrap.
      this.clearSession();
    }
  }

  private clearSession(): void {
    this.accessToken = undefined;
    localStorage.removeItem('token');
    localStorage.removeItem('roles');
  }

  decodeToken(token: string): any {
    return jwtDecode(token);
  }

  get tokenIsSet(): boolean {
    return !!this.accessToken;
  }

  public get token(): string | undefined {
    return this.accessToken;
  }

  updatePassword(data: {
    newPassword: string;
    email: string;
  }): Observable<any> {
    const verificationToken = localStorage.getItem('verificationToken');
    if (!data.email) {
      return throwError(() => new Error('Email not found'));
    }
    if (!verificationToken) {
      return throwError(() => new Error('Verification token not found'));
    }
    // add this to the req
    const payload = JSON.stringify({ email: data.email, verificationToken });
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('User', payload);

    const url = `${this.apiUrl}/users/password`;
    return this.http.put(url, data, { headers: headers });
  }

  async loadUser(): Promise<User | null> {
    if (!this.tokenIsSet) {
      this._user$.next(null);
      // throw new Error("No token found");
      return null;
    }
    try {
      const user = await firstValueFrom(
        this.http.get<User>(`${this.apiUrl}/users/account`),
      );

      // history is optional (new users have none yet) — don't fail login on it
      try {
        const history = await firstValueFrom(
          this.http.get<{ history: []; userId: string }>(
            environment.apiUrl + `/results/history`,
          ),
        );
        if (history && history.history) {
          user.history = history.history;
        }
      } catch (historyError) {
        if (!environment.production) {
          console.warn('[AuthService] history unavailable:', historyError);
        }
      }

      this.setRoles(user?.roles || []);
      this.user = user;
      this._user$.next(user);
      return user;
    } catch (error) {
      if (!environment.production) {
        console.error('[AuthService] Error loading user:', error);
      }
      if (!this.user) {
        this._user$.next(null);
      }
      // Do not rethrow: AppComponent calls loadUser() without await/catch; a
      // rejected promise can surface as an unhandled rejection and break the UI.
      return null;
    }
  }

  get currentUser(): User | null {
    return this._user$.value;
  }

  public checkRole(neededRole: UserRole): boolean {
    return (
      !!this.user &&
      (this.isAdmin() === true || this.user.roles.includes(neededRole))
    );
  }

  public isAdmin() {
    return this.user?.roles.includes(UserRole.Admin);
  }

  redirectUser(): void {
    const roles = this.getRoles();

    if (roles.includes('Admin')) {
      this.router.navigate(['/admin/dashboard']);
    } else {
      this.router.navigate(['/user/dashboard']);
    }
  }

  public setRoles(roles: string[]): void {
    localStorage.setItem('roles', JSON.stringify(roles));
  }

  getRoles(): string[] {
    try {
      const roles = localStorage.getItem('roles');
      return roles ? JSON.parse(roles) : [];
    } catch (error) {
      return [];
    }
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unknown error occurred!';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(() => new Error(errorMessage));
  }

  matchingRoles(requiredRoles: UserRole[]) {
    return requiredRoles.some(
      (role: UserRole) => this.user?.roles && this.user.roles.includes(role),
    );
  }

  //TODO: Move search history management to dedicated SearchHistoryService
  //Author: ashiqur-rahman | Date: 2025-10-13
  //Reason: AuthService should not handle search history and query building (228 lines of code)
  saveHistoryData(historyData: any): Observable<any> {
    const url = environment.apiUrl + '/results/history';

    // Send exactly what was passed - a simple array of history entries
    // Send the update to the backend
    return this.http
      .post<{ history?: []; userId: string }>(url, historyData)
      .pipe(
        tap((response: { history?: []; userId: string }) => {
          // Update the BehaviorSubject with the new data from the response when available
          if (response?.history && this.user) {
            this.user.history = response.history;
            this._user$.next(this.user);
          }
        }),
        catchError((error) => {
          console.error('Error saving history:', error);
          return throwError(() => new Error('Failed to save history data'));
        }),
      );
  }
  // Add a history entry
  addHistoryEntry(changes: any, description: string): Observable<any> {
    // Create a new entry with the properly formatted fields to match backend expectations
    const newEntry = {
      action: description, // Use description as the action field
      date: new Date().toISOString(),
      filterByDate: true,
      status: 'PUBLISHED',
      zip: changes.zip || changes.postalCode || null,
      rent:
        typeof changes.rent === 'number'
          ? changes.rent
          : typeof changes.homeCosts === 'number'
            ? changes.homeCosts
            : null,
      income: typeof changes.income === 'number' ? changes.income : null,
      jobRelatedSituation:
        typeof changes.jobRelatedSituation === 'number'
          ? changes.jobRelatedSituation
          : null,

      // Map fields from changes to the correct backend field names
      // Parent information
      parentAge: changes.parentAge || null,
      parentGender: changes.parentGender || null,

      // Children information
      // `||` treats an explicit "no children" (0) as falsy and discards it —
      // same class of bug the isPregnant fix below already guards against.
      childrenCount: changes.childrenCount ?? null,
      // `|| []` never clears: the history merge skips empty arrays as "not submitted".
      childrenAgeGroups: changes.childrenAgeGroups ?? null,
      // Preserve null ("not answered yet") distinct from a real false —
      // `|| false` would otherwise make an unanswered question look like
      // an explicit "no" for offer matching and dashboard progress.
      isPregnant: changes.isPregnant ?? null,

      // Life situation
      relationship: changes.relationship || null,
      isVictimOfViolence: changes.isVictimOfViolence ?? null,
      isRefugee: changes.isRefugee ?? null,

      // Insurance
      insurance: changes.insurance || null,
      incomeDetails: changes.incomeDetails ?? undefined,
    };

    // Create a simple history array with just this entry
    const historyData = {
      history: [newEntry],
    };

    return this.saveHistoryData(historyData).pipe(
      tap(() => this.prependHistoryEntry(newEntry)),
    );
  }

  private prependHistoryEntry(entry: any): void {
    if (!this.user) return;

    const existingHistory = Array.isArray(this.user.history)
      ? this.user.history
      : [];
    const updatedHistory = [entry, ...existingHistory].slice(0, 50);
    this.user.history = updatedHistory;
    this._user$.next(this.user);
  }

  dismissOnboardingBanner(): Observable<void> {
    return this.http
      .patch<void>(`${this.apiUrl}/users/account/dismiss-onboarding-banner`, {})
      .pipe(
        tap(() => {
          if (!this.user) return;
          this.user = {
            ...this.user,
            onboardingBannerDismissedAt: new Date().toISOString(),
          };
          this._user$.next(this.user);
        }),
        catchError((error) => {
          console.error('Error dismissing onboarding banner:', error);
          return throwError(
            () => new Error('Failed to dismiss onboarding banner'),
          );
        }),
      );
  }

  get lastHistoryEntry(): any {
    if (this.user?.history?.length) {
      return this.user.history[0];
    }
    return null;
  }

  getQueryParams(
    categoryId?: string,
    limit = 20,
    skip = 0,
  ): QueryFilterDTO | null {
    const currentSearch = this.lastHistoryEntry;

    const answers: FinderAnswers | null = currentSearch
      ? {
          parentGender: currentSearch.parentGender,
          parentAge: currentSearch.parentAge,
          childrenCount: currentSearch.childrenCount,
          childrenAgeGroups: currentSearch.childrenAgeGroups,
          isPregnant: currentSearch.isPregnant,
          isVictimOfViolence: currentSearch.isVictimOfViolence,
          isRefugee: currentSearch.isRefugee,
          rent: currentSearch.rent,
          zip: Array.isArray(currentSearch.zip)
            ? currentSearch.zip[0]
            : currentSearch.zip,
          income: currentSearch.income,
          relationship: currentSearch.relationship,
          insurance: currentSearch.insurance,
          jobRelatedSituation: currentSearch.jobRelatedSituation,
        }
      : null;

    const filter = buildResultsQueryFilter(answers, {
      limit,
      skip,
      categoryId,
      language: this.translate.currentLang as LanguageKey,
    });

    return omitUndefined({
      ...filter,
      status: filter.status as string,
      filterByDate: filter.filterByDate as boolean,
      zip: filter.zip ? [filter.zip] : undefined,
      insurance: filter.insurance as unknown as QueryFilterDTO['insurance'],
    }) as QueryFilterDTO;
  }
}
